// Centralized query key factory. Each domain owns a sub-tree so callers can
// invalidate coarsely (e.g. queryClient.invalidateQueries({ queryKey: queryKeys.roles.all }))
// or target a specific entity (queryKeys.roles.detail(id)).

export const queryKeys = {
  roles: {
    all: ['roles'] as const,
    lists: () => [...queryKeys.roles.all, 'list'] as const,
    list: (page: number, pageSize: number) =>
      [...queryKeys.roles.lists(), { page, pageSize }] as const,
    detail: (id: string) => [...queryKeys.roles.all, 'detail', id] as const,
    forUser: (userId: string) => [...queryKeys.roles.all, 'user', userId] as const,
  },
  permissions: {
    all: ['permissions'] as const,
    groups: () => [...queryKeys.permissions.all, 'groups'] as const,
  },
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (page: number, pageSize: number, search: string) =>
      [...queryKeys.users.lists(), { page, pageSize, search }] as const,
    detail: (id: string) => [...queryKeys.users.all, 'detail', id] as const,
  },
  customers: {
    all: ['customers'] as const,
    lists: () => [...queryKeys.customers.all, 'list'] as const,
    list: (page: number, pageSize: number, search: string, tier: number | undefined) =>
      [...queryKeys.customers.lists(), { page, pageSize, search, tier }] as const,
    detail: (id: string) => [...queryKeys.customers.all, 'detail', id] as const,
    contacts: (customerId: string) =>
      [...queryKeys.customers.all, 'contacts', customerId] as const,
  },
  tickets: {
    all: ['tickets'] as const,
    lists: () => [...queryKeys.tickets.all, 'list'] as const,
    list: (page: number, pageSize: number, filters: Record<string, string>) =>
      [...queryKeys.tickets.lists(), { page, pageSize, filters }] as const,
    detail: (id: string) => [...queryKeys.tickets.all, 'detail', id] as const,
    sla: (id: string) => [...queryKeys.tickets.all, 'sla', id] as const,
    timeLogs: (id: string) => [...queryKeys.tickets.all, 'timeLogs', id] as const,
    timeSummary: (id: string) => [...queryKeys.tickets.all, 'timeSummary', id] as const,
    history: (id: string) => [...queryKeys.tickets.all, 'history', id] as const,
    relations: (id: string) => [...queryKeys.tickets.all, 'relations', id] as const,
  },
  relationTypes: {
    all: ['relationTypes'] as const,
  },
  responseTemplates: {
    all: ['responseTemplates'] as const,
    list: (activeOnly: boolean) => [...queryKeys.responseTemplates.all, { activeOnly }] as const,
  },
  templates: {
    all: ['templates'] as const,
    tickets: (activeOnly: boolean) => [...queryKeys.templates.all, 'tickets', { activeOnly }] as const,
  },
  customFields: {
    all: ['customFields'] as const,
    byEntity: (entityType: number, activeOnly: boolean) =>
      [...queryKeys.customFields.all, { entityType, activeOnly }] as const,
  },
  projects: {
    all: ['projects'] as const,
    lists: () => [...queryKeys.projects.all, 'list'] as const,
    list: (query: Record<string, unknown>) => [...queryKeys.projects.lists(), query] as const,
    detail: (id: string) => [...queryKeys.projects.all, 'detail', id] as const,
    statistics: () => [...queryKeys.projects.all, 'statistics'] as const,
    statuses: () => [...queryKeys.projects.all, 'statuses'] as const,
    priorities: () => [...queryKeys.projects.all, 'priorities'] as const,
  },
  projectTasks: {
    all: (projectId: string) => ['projectTasks', projectId] as const,
    board: (projectId: string) => [...queryKeys.projectTasks.all(projectId), 'board'] as const,
    list: (projectId: string, query: Record<string, unknown>) =>
      [...queryKeys.projectTasks.all(projectId), 'list', query] as const,
    everything: (projectId: string) => [...queryKeys.projectTasks.all(projectId), 'all'] as const,
    statistics: (projectId: string) =>
      [...queryKeys.projectTasks.all(projectId), 'statistics'] as const,
    statuses: (projectId: string) =>
      [...queryKeys.projectTasks.all(projectId), 'statuses'] as const,
    priorities: (projectId: string) =>
      [...queryKeys.projectTasks.all(projectId), 'priorities'] as const,
  },
  dashboard: {
    all: ['dashboard'] as const,
    stats: () => [...queryKeys.dashboard.all, 'stats'] as const,
    adminStats: () => [...queryKeys.dashboard.all, 'adminStats'] as const,
  },
} as const;
